"use client";

import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useCallback } from "react";
import type { UserRole } from "@/generated/prisma";

export function useAuth() {
  const { data: session, status } = useSession();
  const router = useRouter();

  const isLoading = status === "loading";
  const isAuthenticated = status === "authenticated";

  // User data with type safety
  const user = session?.user ?? null;

  // Check if user has a specific role
  const hasRole = useCallback(
    (role: UserRole) => {
      return user?.role === role;
    },
    [user?.role]
  );

  // Check if user has any of the specified roles
  const hasAnyRole = useCallback(
    (roles: UserRole[]) => {
      return user?.role ? roles.includes(user.role) : false;
    },
    [user?.role]
  );

  // Check if user is an owner
  const isOwner = useCallback(() => {
    return user?.role === "OWNER";
  }, [user?.role]);

  // Check if user is staff
  const isStaff = useCallback(() => {
    return user?.role === "STAFF";
  }, [user?.role]);

  // Check if user is a client
  const isClient = useCallback(() => {
    return user?.role === "CLIENT";
  }, [user?.role]);

  // Require authentication - redirect to login if not authenticated
  const requireAuth = useCallback(
    (redirectTo: string = "/login") => {
      if (!isAuthenticated && !isLoading) {
        router.push(redirectTo);
      }
    },
    [isAuthenticated, isLoading, router]
  );

  // Require specific role - redirect if user doesn't have the role
  const requireRole = useCallback(
    (role: UserRole, redirectTo: string = "/dashboard") => {
      if (isAuthenticated && !hasRole(role)) {
        router.push(redirectTo);
      }
    },
    [isAuthenticated, hasRole, router]
  );

  // Get organization ID for multi-tenant operations
  const organizationId = user?.organizationId ?? null;

  return {
    user,
    session,
    isLoading,
    isAuthenticated,
    organizationId,
    hasRole,
    hasAnyRole,
    isOwner: isOwner(),
    isStaff: isStaff(),
    isClient: isClient(),
    requireAuth,
    requireRole,
  };
}

// Type for the return value of useAuth
export type UseAuthReturn = ReturnType<typeof useAuth>;
